// Blender-authored, render-only dressing. This catalog is separate from the
// baked surface catalog: no simulation shape or collider is inferred from art.
import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";

export type PortKitId =
  | "docking-gate"
  | "freight-container"
  | "freight-container-lod"
  | "pressure-vessel"
  | "port-kit-preview";
export interface PortKitEntry {
  url: string;
  bytes: number;
  triangles: number;
  materials: number;
  meshes: number;
}
interface PortKitManifest {
  version: number;
  assets: Record<PortKitId, PortKitEntry>;
}
const templates = new Map<PortKitId, THREE.Group>();
const jobs = new Map<PortKitId, Promise<void>>();
let catalog: Promise<PortKitManifest> | undefined;

export function portKitManifest(): Promise<PortKitManifest> {
  catalog ??= fetch("/assets/port-kit/manifest.json", { cache: "no-cache" }).then(
    async (response) => {
      if (!response.ok)
        throw new Error(`Port kit manifest ${response.status}; build the Blender kit first.`);
      const manifest = (await response.json()) as PortKitManifest;
      if (manifest.version !== 1 || !manifest.assets)
        throw new Error("Unsupported port kit manifest.");
      return manifest;
    },
  );
  return catalog;
}

export async function loadPortKit(ids: readonly PortKitId[]): Promise<void> {
  const manifest = await portKitManifest();
  await Promise.all(
    ids.map((id) => {
      let job = jobs.get(id);
      if (!job) {
        job = (async () => {
          const entry = manifest.assets[id];
          if (!entry?.url?.startsWith("/assets/port-kit/"))
            throw new Error(`Missing port kit asset: ${id}`);
          const gltf = await new GLTFLoader().loadAsync(entry.url);
          gltf.scene.updateMatrixWorld(true);
          gltf.scene.traverse((object) => {
            if (!(object instanceof THREE.Mesh)) return;
            object.castShadow = true;
            object.receiveShadow = true;
          });
          templates.set(id, gltf.scene);
        })();
        jobs.set(id, job);
      }
      return job;
    }),
  );
}

/** Geometry and materials remain owned by the cache, like the baked surfaces. */
export function clonePortKit(id: PortKitId): THREE.Group {
  const template = templates.get(id);
  if (!template) throw new Error(`Port kit ${id} was not loaded at the boot gate.`);
  return template.clone(true);
}

export interface PortKitPlacement {
  x: number;
  y: number;
  z: number;
  yaw: number;
  width: number;
  height: number;
  depth: number;
}

/** One draw per authored primitive, across every existing container position. */
export function instancePortKit(
  id: PortKitId,
  placements: readonly PortKitPlacement[],
): THREE.Group {
  const template = templates.get(id);
  if (!template) throw new Error(`Port kit ${id} was not loaded at the boot gate.`);
  const group = new THREE.Group();
  const bounds = new THREE.Box3().setFromObject(template);
  const size = bounds.getSize(new THREE.Vector3());
  const center = bounds.getCenter(new THREE.Vector3());
  const bottomCenter = new THREE.Matrix4().makeTranslation(-center.x, -bounds.min.y, -center.z);
  const transform = new THREE.Object3D();
  const matrix = new THREE.Matrix4();
  template.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    const material = Array.isArray(object.material)
      ? object.material.map((entry) => entry.clone())
      : object.material.clone();
    const mesh = new THREE.InstancedMesh(object.geometry, material, placements.length);
    mesh.userData.portKit = true;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    placements.forEach((placement, index) => {
      transform.position.set(placement.x, placement.y, placement.z);
      transform.rotation.set(0, placement.yaw, 0);
      transform.scale.set(
        placement.width / size.x,
        placement.height / size.y,
        placement.depth / size.z,
      );
      transform.updateMatrix();
      matrix.copy(transform.matrix).multiply(bottomCenter).multiply(object.matrixWorld);
      mesh.setMatrixAt(index, matrix);
    });
    mesh.computeBoundingSphere();
    group.add(mesh);
  });
  return group;
}

/** The city probe's global gain is calibrated for the forge's custom BRDF.
 * Imported PBR keeps its authored albedo and gets an explicit, local probe gain.
 */
export function bindPortKitEnvironment(
  root: THREE.Object3D,
  environment: THREE.Texture,
  intensity: number,
): void {
  root.traverse((object) => {
    if (!(object instanceof THREE.Mesh) || !object.userData.portKit) return;
    const materials = Array.isArray(object.material) ? object.material : [object.material];
    for (const material of materials) {
      if (!(material instanceof THREE.MeshStandardMaterial)) continue;
      material.envMap = environment;
      material.envMapIntensity = intensity;
      material.needsUpdate = true;
    }
  });
}
